import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/api";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import type { FindCityAirportsQueryOutputDto } from "@/types/codegen";
import type { CityWithAirports } from "@/types/types";
import { toTitleCase } from "@/utils/titleCase";

export function useAirportSearch(searchQuery: string) {
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 500);

  return useQuery({
    queryKey: ["airports-search", debouncedSearch] as const,
    queryFn: ({ signal }) =>
      api.get<FindCityAirportsQueryOutputDto>(`/mappings/cities/airports`, {
        params: { searchQuery: debouncedSearch },
        signal,
      }),
    select: (response): CityWithAirports[] => {
      const { cities } = response.data.data;

      return cities.map((city) => ({
        ...city,
        // IRIX returns city names in uppercase, so we need to convert them to title case
        name: toTitleCase(city.name),
        type: "CITY", // this is only needed for type errors, this value already has "CITY" type but generated types are set to 'string'
        airports: city.airports.map((airport) => ({
          ...airport,
          // cityName and countryName added to each airport for easier access later
          cityName: toTitleCase(city.name),
          countryName: city.countryName,
          type: "AIRPORT", // this is only needed for type errors, this value already has "AIRPORT" type but generated types are set to 'string'
        })),
      }));
    },
    enabled: debouncedSearch.length > 2,
    staleTime: 1000 * 60 * 60 * 24, // fetch fresh translations after 24 hours
  });
}
